import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthProvider";
import { useT } from "@/i18n";
import Logo from "@/components/common/Logo";
import ThemeToggle from "@/components/common/ThemeToggle";
import LanguageToggle from "@/components/common/LanguageToggle";
import GoogleButton from "@/components/common/GoogleButton";

export default function Login() {
  const t = useT();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden flex-col justify-between bg-primary p-12 text-primary-foreground lg:flex">
        <Logo />
        <div className="dotgrid absolute inset-0 opacity-[0.06]" />
        <div className="relative z-10 max-w-md">
          <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tighter">
            {t("landing.heroTitle")}
          </h2>
          <p className="mt-4 text-primary-foreground/70 leading-relaxed">{t("landing.heroSubtitle")}</p>
        </div>
        <p className="relative z-10 font-mono text-xs text-primary-foreground/50">// {t("common.appTagline")}</p>
      </div>

      <div className="flex flex-col">
        <div className="flex items-center justify-between p-6">
          <div className="lg:hidden"><Logo /></div>
          <div className="ml-auto flex items-center gap-2">
            <LanguageToggle />
            <ThemeToggle />
          </div>
        </div>
        <div className="flex flex-1 items-center justify-center p-6">
          <div className="w-full max-w-sm">
            <h1 className="font-display text-3xl font-extrabold tracking-tighter">{t("auth.loginTitle")}</h1>
            <p className="mt-2 text-muted-foreground">{t("auth.loginSubtitle")}</p>

            <div className="mt-8"><GoogleButton /></div>
            <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-muted-foreground">
              <span className="h-px flex-1 bg-border" />{t("auth.or")}<span className="h-px flex-1 bg-border" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium">{t("auth.email")}</label>
                <input
                  data-testid="login-email" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm outline-none transition-colors focus:ring-2 focus:ring-brand"
                />
              </div>
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-sm font-medium">{t("auth.password")}</label>
                  <Link to="/forgot-password" className="text-xs text-brand hover:underline" data-testid="forgot-link">
                    {t("auth.forgotPassword")}
                  </Link>
                </div>
                <input
                  data-testid="login-password" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm outline-none transition-colors focus:ring-2 focus:ring-brand"
                />
              </div>
              <button
                data-testid="login-submit" type="submit" disabled={loading}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-primary font-medium text-primary-foreground transition-transform hover:opacity-90 active:scale-[0.98] disabled:opacity-60"
              >
                {loading ? t("common.loading") : t("auth.login")}
                {!loading && <ArrowRight className="h-4 w-4" />}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              {t("auth.noAccount")}{" "}
              <Link to="/register" className="font-medium text-foreground hover:underline" data-testid="to-register">
                {t("auth.signUp")}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
